import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { IClientSettings } from '@common/clientSettings';
import { ActionResponse } from '@common/interfaces/action';
import { snapshotManager } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { en_US, it_IT, NzI18nService, pt_BR } from 'ng-zorro-antd/i18n';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { combineLatest, fromEvent, interval } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppService } from './app.service';
import { WebpushNotificationService } from './core/services/wpn.service';
import { AUTH_SNAPSHOT_STORAGE_KEY, resolveAuthTabSync } from './project/auth/auth-tab-sync';
import { AuthService } from './project/auth/auth.service';
import { AppEventsService } from './project/service/appEvents.service';
import { ClientMessagesService } from './project/service/clientMessages.service';
import { UiService } from './project/state/ui/ui.service';
import { RealtimeV2RuntimeService } from './realtime-v2';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, AfterViewInit {
  updProgress = 0;
  letItSnow = false;
  // Reload già deciso per un cambio di sessione avvenuto in un'altra tab: gli eventi successivi non
  // hanno più nulla da dire, e il reload è asincrono, quindi senza questa guardia verrebbero
  // elaborati comunque (il logout ne emette due: reset dello snapshot e rimozione della chiave).
  private reloadingForSessionChange = false;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private _cdr: ChangeDetectorRef,
    private http: HttpClient,
    public appService: AppService,
    private update: SwUpdate,
    private nzns: NzNotificationService,
    private wpns: WebpushNotificationService,
    private authService: AuthService,
    private translate: TranslateService,
    private nzI18n: NzI18nService,
    private uiService: UiService,
    private aes: AppEventsService,
    private cms: ClientMessagesService,
    private realtimeV2Runtime: RealtimeV2RuntimeService
  ) {
    this.appService.settings$
      .pipe(
        filter((settings) => !!settings && !!settings.i18n),
        take(1),
        tap((settings) => this.setupLanguage(settings))
      )
      .subscribe();

    if (environment.production) {
      this.wpns.clicks$.subscribe((c) => {
        console.log('[WP] click', c);
      });

      this.wpns.events$.subscribe((evt) => {
        console.log('[WP] event', evt);
      });

      this.wpns.messages$.subscribe((msg) => {
        console.log('[WP] message', msg);
      });

      this.wpns.notifications$.subscribe((nts) => {
        console.log('[WP] notification', nts);
      });
    }

    const MM_DD = new Date().toISOString().substring(5, 10);
    this.letItSnow = ['12-24', '12-25', '12-26', '12-31', '01-01', '01-06'].includes(MM_DD); // nevica !

    this.authService.authQuery.user$
      .pipe(
        filter((user) => !!user),
        untilDestroyed(this)
      )
      .subscribe(({ settings }) => {
        if (settings.language && settings.language !== this.translate.currentLang) {
          const locale = settings.language.toLowerCase();
          this.translate.use(locale);
          this.setNzLocale(locale);
        }
      });
  }

  /**
   * Allinea questa tab all'auth scritto da un'altra tab dello stesso browser. La decisione vive in
   * `resolveAuthTabSync`, qui resta solo l'esecuzione.
   */
  private syncAuthFromOtherTab(snapshot: string | null): void {
    if (this.reloadingForSessionChange) {
      return;
    }

    const currentAuth = this.authService.authQuery.getValue();
    const outcome = resolveAuthTabSync(snapshot, currentAuth.accessToken, currentAuth.cacheEpoch);
    switch (outcome.action) {
      case 'ignore':
        return;
      case 'end-session':
        this.reloadForSessionChange('sessione chiusa da un\'altra tab');
        return;
      case 'session-started':
        this.reloadForSessionChange('login effettuato in un\'altra tab');
        return;
      case 'session-changed':
        this.reloadForSessionChange('utenza cambiata in un\'altra tab');
        return;
      case 'apply':
        try {
          // Oggetto e non stringa: vedi `resolveAuthTabSync` per il perché.
          snapshotManager.setStoresSnapshot(outcome.stores, { skipStorageUpdate: true });
        } catch (error) {
          // Un throw qui ucciderebbe la sottoscrizione all'evento `storage`, e con essa ogni futuro
          // allineamento fra tab: va contenuto anche se non dovrebbe accadere su input validato.
          console.error('[APP] applicazione dello snapshot auth fallita', error);
        }
        return;
    }
  }

  /**
   * Riallinea questa tab a un cambio di sessione deciso da un'altra tab, ricaricando.
   *
   * Reload e non navigazione programmatica, in entrambe le direzioni:
   * - all'uscita, chiamare `authService.logout()` riscriverebbe il localStorage e ripeterebbe la POST
   *   di logout, generando nuovi eventi `storage` verso le altre tab — un rimbalzo fra tab per un
   *   logout già avvenuto;
   * - all'ingresso, navigare richiederebbe di decidere qui la destinazione, duplicando
   *   `AlreadyLoggedInGuard`, su un'app che si era avviata da slogata.
   *
   * Il reload riparte da `createInitialAuthState()`, che legge il token appena scritto dall'altra tab,
   * e lascia decidere alle guardie già esistenti: senza token `AuthGuardService` porta al login, con
   * token `AlreadyLoggedInGuard` porta a `project`. In più garantisce che non sopravvivano socket,
   * sottoscrizioni o store a metà. È lo stesso modo in cui `logout()` chiude la propria tab.
   */
  private reloadForSessionChange(reason: string): void {
    this.reloadingForSessionChange = true;
    console.log(`[APP] ${reason}, ricarico`);
    window.location.reload();
  }

  private setNzLocale(locale: string) {
    switch (locale) {
      case 'it':
        this.nzI18n.setLocale(it_IT);
        break;
      case 'br':
        this.nzI18n.setLocale(pt_BR);
        break;
      default:
        this.nzI18n.setLocale(en_US);
        break;
    }
  }

  async ngOnInit() {
    await this.updManager();

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((event) => event.key === AUTH_SNAPSHOT_STORAGE_KEY))
      .subscribe((event) => this.syncAuthFromOtherTab(event.newValue));

    this.activatedRoute.queryParams.subscribe(async (params) => {
      if (params.action_uid) {
        try {
          const rv: ActionResponse = await this.http.get(`${environment.apiUrl}/api/actions/execute/${params.action_uid}`).toPromise();
          if (rv.message) this.nzns.create('success', 'Operazione effettuata.', rv.message);
          else this.nzns.create('error', 'Operazione non riuscita.', rv.err);
        } catch (err) {
          this.nzns.create('error', 'Operazione non riuscita.', err.message);
        }
      }
    });

    // RXJS :: controllo ogni 5 secondi solo se non sono loggato
    combineLatest([interval(5000), this.authService.authQuery.loggedIn$])
      .pipe(
        filter(([v, li]) => !li),
        map(([v, li]) => {
          return v;
        })
      )
      .subscribe(async (v) => {
        await this.appService.check();
      });
  }

  private setupLanguage(settings: IClientSettings) {
    const languages = settings.i18n.langs?.map((c) => c.toLowerCase()) || ['it', 'en'];
    // settings.json may carry uppercase codes ("IT"), but translation files are lowercase (assets/i18n/it.json):
    // an uppercase code 404s and ngx-translate silently stays on its 'en' fallback
    const mainLang = (settings.i18n.mainLang || 'it').toLowerCase();
    const defaultLang = settings.i18n.defaultLang || 'it';
    const browserLang = this.translate.getBrowserLang();

    let langToUse = (defaultLang === '_BROWSER' ? browserLang || mainLang : defaultLang).toLowerCase();
    if (!languages.includes(langToUse)) {
      langToUse = mainLang;
    }
    this.translate.setDefaultLang(mainLang);
    this.translate.addLangs(languages);
    this.translate.use(langToUse);
    this.setNzLocale(langToUse);
    this.uiService.langReady(true);
  }

  async updManager() {
    if (!this.update.isEnabled) {
      return;
    }
    const s = environment.production ? 30 : 5;
    console.log('Checking updates every', s, 'seconds');
    interval(s * 1000).subscribe(() => {
      this.update.checkForUpdate();
    });

    this.update.available.subscribe(async (event) => {
      //console.log(`current`, event.current, `available`, event.available);
      if (this.appService.isVersionOk) this.nzns.create('info', `Aggiornamento applicazione`, `E' disponibile una nuova versione dell'applicazione`);
      await this.update.activateUpdate();
      setTimeout(
        () => {
          location.reload();
        },
        environment.production ? 5000 : 1
      );

      this.updProgress = 80;

      interval(250).subscribe(() => {
        this.updProgress++;
      });
    });

    this.appService.versionOk$.subscribe((value) => {
      if (value) {
        this.updProgress = 0;
      } else {
        interval(500)
          .pipe(takeUntil(this.update.available))
          .subscribe(() => {
            this.updProgress++;

            if (this.updProgress > 100) location.reload();
          });
      }
    });
  }
  ngAfterViewInit() {
    this._cdr.detectChanges();
  }
}
